import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import Box from '../../../shared/components/Box/Box';
import Button from '../../../shared/components/Button/Button';
import Input from '../../../shared/components/Input/Input';
import Typography from '../../../shared/components/Typography/Typography';
import Dialog from '../../../shared/components/Dialog/Dialog';
import './gedPage.scss';
import {
  getInterventionLdaPublicationTarget,
  getLdaPublicationTarget,
  publishInterventionLdaFile,
  publishLdaFile,
} from '../../lda/lda.api';
import {
  getNextcloudAccount,
  getNextcloudDocuments,
  getNextcloudStatus,
  testNextcloudAccount,
  updateNextcloudAccount,
} from './ged.api';

const defaultNextcloudPath = '/Projets';

const normalizeGedPath = (value = '/') => {
  const nextPath = `/${String(value || '/').replace(/^\/+|\/+$/g, '')}`.replace(/\/+/g, '/');
  return nextPath === '/' ? '/' : nextPath.replace(/\/$/, '');
};

const formatDate = (value) => value && !Number.isNaN(Date.parse(value))
  ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium' }).format(new Date(value)) : '—';
const formatSize = (value) => value < 1024 ? `${value} o`
  : value < 1048576 ? `${(value / 1024).toFixed(1)} Ko` : `${(value / 1048576).toFixed(1)} Mo`;

const spaceGroup = (name = '') => {
  if (/^(Affaires|Projets(?: \(\d+\))?|Interventions)$/i.test(name)) return 'Activité';
  if (/^(Documents|Photos|Templates)$/i.test(name)) return 'Ressources';
  if (/^RH$/i.test(name)) return 'Espace protégé';
  return null;
};
const documentType = (document) => document.isFolder ? 'Dossier'
  : (document.name?.includes('.') ? document.name.split('.').pop().toUpperCase() : 'Fichier');

function GedPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const requestSequence = useRef(0);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [kind, setKind] = useState('all');
  const [sort, setSort] = useState('name');
  const [view, setView] = useState('list');
  const [infoDocument, setInfoDocument] = useState(null);
  const [actionMessage, setActionMessage] = useState('');
  const [accountOpen, setAccountOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [currentPath, setCurrentPath] = useState(defaultNextcloudPath);
  const [nextcloudStatus, setNextcloudStatus] = useState({
    connected: false,
    loading: true,
    error: '',
    rootPath: defaultNextcloudPath,
  });
  const [search, setSearch] = useState('');
  const [nextcloudAccount, setNextcloudAccount] = useState({
    nextcloudUsername: '',
    nextcloudAppPassword: '',
    nextcloudRootPath: defaultNextcloudPath,
    hasNextcloudCredentials: false,
  });
  const [accountStatus, setAccountStatus] = useState(null);
  const [publicationTarget, setPublicationTarget] = useState(null);
  const [publicationFile, setPublicationFile] = useState(null);
  const [publicationState, setPublicationState] = useState({ loading: false, error: '', success: null });

  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const gedMode = searchParams.get('mode');
  const ldaLineId = searchParams.get('ldaLineId');
  const projectId = searchParams.get('projectId');
  const interventionId = searchParams.get('interventionId');
  const requestedPath = searchParams.get('path');

  const loadNextcloudDocuments = useCallback(async (pathOverride) => {
    const sequence = ++requestSequence.current;
    try {
      setNextcloudStatus((current) => ({ ...current, loading: true, error: '' }));
      const status = await getNextcloudStatus();
      const requestedPath = normalizeGedPath(pathOverride || status.rootPath || currentPath || defaultNextcloudPath);
      const data = await getNextcloudDocuments(requestedPath);
      const nextDocuments = Array.isArray(data.documents) ? data.documents : [];
      const nextPath = normalizeGedPath(data.path || requestedPath);

      if (sequence !== requestSequence.current) return;
      setLastLoaded(new Date());
      setDocuments(nextDocuments);
      setCurrentPath(nextPath);
      setNextcloudStatus({
        connected: true,
        loading: false,
        error: '',
        rootPath: status.rootPath || nextPath,
      });
    } catch (error) {
      if (sequence !== requestSequence.current) return;
      setDocuments([]);
      setNextcloudStatus({
        connected: false,
        loading: false,
        error: error.message || 'Connexion NextCloud indisponible',
        rootPath: nextcloudStatus.rootPath || defaultNextcloudPath,
      });
    }
  }, [currentPath, nextcloudStatus.rootPath]);

  useEffect(() => {
    loadNextcloudDocuments(requestedPath || undefined);
  }, [requestedPath]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (gedMode !== 'publish' || (!projectId && !interventionId) || !ldaLineId) {
      setPublicationTarget(null);
      return;
    }

    let active = true;
    const loadPublicationTarget = async () => {
      try {
        setPublicationState({ loading: true, error: '', success: null });
        const target = projectId
          ? await getLdaPublicationTarget(projectId, ldaLineId)
          : await getInterventionLdaPublicationTarget(interventionId, ldaLineId);
        if (!active) return;
        setPublicationTarget(target);
        setPublicationState({ loading: false, error: '', success: null });
      } catch (error) {
        if (!active) return;
        setPublicationTarget(null);
        setPublicationState({ loading: false, error: error.message || 'Impossible de déterminer le dossier GED cible.', success: null });
      }
    };

    loadPublicationTarget();
    return () => { active = false; };
  }, [gedMode, projectId, interventionId, ldaLineId]);

  useEffect(() => {
    const loadNextcloudAccount = async () => {
      try {
        const account = await getNextcloudAccount();
        setNextcloudAccount((current) => ({
          ...current,
          nextcloudUsername: account.nextcloudUsername || '',
          nextcloudRootPath: account.nextcloudRootPath || defaultNextcloudPath,
          hasNextcloudCredentials: Boolean(account.hasNextcloudCredentials),
        }));
      } catch (error) {
        setAccountStatus({
          severity: 'error',
          message: error.message || 'Impossible de charger le compte NextCloud.',
        });
      }
    };

    loadNextcloudAccount();
  }, []);

  const folders = useMemo(
    () => documents
      .filter((document) => document.isFolder)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [documents]
  );

  const files = useMemo(
    () => documents
      .filter((document) => !document.isFolder)
      .sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''))),
    [documents]
  );

  const displayedDocuments = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const mergedDocuments = [...folders, ...files].filter((document) => kind === 'all'
      || (kind === 'folders' ? document.isFolder : !document.isFolder));
    if (sort === 'recent') mergedDocuments.sort((a, b) => (Date.parse(b.updatedAt) || 0) - (Date.parse(a.updatedAt) || 0));

    if (!needle) {
      return mergedDocuments;
    }

    return mergedDocuments.filter((document) =>
      [
        document.name,
        document.folder,
        document.nextCloudPath,
        document.status,
        documentType(document),
        ...(document.lda || []).flatMap((line) => [line.title, line.codeGed, line.lot, line.documentType, line.status]),
      ].some((value) => String(value || '').toLowerCase().includes(needle))
    );
  }, [files, folders, search, kind, sort]);

  const rootPath = normalizeGedPath(nextcloudStatus.rootPath);
  const atRoot = normalizeGedPath(currentPath) === rootPath;
  const showSpaces = atRoot && !search && kind === 'all';
  const listedDocuments = showSpaces
    ? displayedDocuments.filter((document) => !document.isFolder || !spaceGroup(document.name))
    : displayedDocuments;
  const breadcrumbs = useMemo(() => {
    const normalized = normalizeGedPath(currentPath);
    const relative = rootPath !== '/' && (normalized === rootPath || normalized.startsWith(`${rootPath}/`))
      ? normalized.slice(rootPath.length) : normalized;
    const prefix = rootPath !== '/' && (normalized === rootPath || normalized.startsWith(`${rootPath}/`)) ? rootPath : '';
    const parts = relative.split('/').filter(Boolean);
    return parts.map((part, index) => ({ label: part, path: `${prefix}/${parts.slice(0, index + 1).join('/')}` }));
  }, [currentPath, rootPath]);

  const navigateFolder = (path) => {
    const params = new URLSearchParams(location.search);
    params.set('path', path);
    setSearch('');
    if (path === currentPath) loadNextcloudDocuments(path);
    navigate(`${location.pathname}?${params}`);
  };

  const copyDocumentLink = async (document) => {
    try {
      const url = document.isFolder
        ? `${window.location.origin}${location.pathname}?path=${encodeURIComponent(document.nextCloudPath)}` : document.openUrl;
      await navigator.clipboard.writeText(url);
      setActionMessage('Lien copié. Les droits d’accès restent nécessaires.');
    } catch { setActionMessage('Impossible de copier le lien dans ce navigateur.'); }
  };

  const handleAccountChange = (event) => {
    const { name, value } = event.target;
    setNextcloudAccount((current) => ({ ...current, [name]: value }));
  };

  const buildAccountPayload = () => ({
    nextcloudUsername: nextcloudAccount.nextcloudUsername.trim(),
    nextcloudRootPath: nextcloudAccount.nextcloudRootPath.trim() || '/',
    ...(nextcloudAccount.nextcloudAppPassword ? { nextcloudAppPassword: nextcloudAccount.nextcloudAppPassword } : {}),
  });

  const saveNextcloudAccount = async () => {
    try {
      const account = await updateNextcloudAccount(buildAccountPayload());
      const nextRootPath = normalizeGedPath(account.nextcloudRootPath || nextcloudAccount.nextcloudRootPath || defaultNextcloudPath);
      setNextcloudAccount((current) => ({
        ...current,
        nextcloudAppPassword: '',
        nextcloudRootPath: nextRootPath,
        hasNextcloudCredentials: Boolean(account.hasNextcloudCredentials),
      }));
      setAccountStatus({ severity: 'success', message: 'Compte NextCloud enregistre.' });
      setAccountOpen(false);
      await loadNextcloudDocuments(nextRootPath);
    } catch (error) {
      setAccountStatus({ severity: 'error', message: error.message || 'Impossible d enregistrer le compte NextCloud.' });
    }
  };

  const testPersonalNextcloudAccount = async () => {
    try {
      const status = await testNextcloudAccount(buildAccountPayload());
      setAccountStatus({
        severity: 'success',
        message: `Connexion OK sur ${status.rootPath || nextcloudAccount.nextcloudRootPath}.`,
      });
    } catch (error) {
      setAccountStatus({ severity: 'error', message: error.message || 'Connexion NextCloud impossible.' });
    }
  };

  const openDocument = (document) => {
    if (document.isFolder) {
      navigateFolder(document.nextCloudPath);
      return;
    }

    if (document.openUrl) {
      window.open(document.openUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const syncCurrentFolder = () => {
    loadNextcloudDocuments(currentPath);
  };

  const publishSelectedLdaFile = async () => {
    if (!publicationFile || (!projectId && !interventionId) || !ldaLineId) return;
    try {
      setPublicationState({ loading: true, error: '', success: null });
      const result = projectId
        ? await publishLdaFile(projectId, ldaLineId, publicationFile)
        : await publishInterventionLdaFile(interventionId, ldaLineId, publicationFile);
      setPublicationState({ loading: false, error: '', success: result });
      setPublicationFile(null);
      setPublicationTarget((current) => current ? { ...current, existingDocumentId: result.documentId } : current);
    } catch (error) {
      setPublicationState({ loading: false, error: error.message || 'La publication dans NextCloud a échoué.', success: null });
    }
  };

  return (
    <Box className="page-container module-page ged-page">
      <div className="ged-page-header">
        <div><Typography variant="h4">GED</Typography>
          <Typography color="text.secondary">Gestion documentaire de l’entreprise</Typography></div>
        <div className="ged-sync-status">
          <span role="status">{nextcloudStatus.loading ? 'Actualisation…' : nextcloudStatus.error
            ? '⚠ Connexion indisponible' : `✓ Actualisé à ${lastLoaded?.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) || '—'}`}</span>
          <details className="ged-actions"><summary aria-label="Options de la GED">⋯</summary><div>
            <button type="button" onClick={syncCurrentFolder} disabled={nextcloudStatus.loading}>Actualiser le dossier</button>
            <button type="button" onClick={() => setAccountOpen(true)}>Compte documentaire</button>
          </div></details>
        </div>
      </div>

      {gedMode === 'publish' && ldaLineId && (
        <section className="module-panel ged-publish-context">
          <Typography variant="h6" mb={1}>Publication LDA</Typography>
          {publicationTarget && (
            <div className="ged-publish-summary">
              <Typography><strong>{publicationTarget.codeGed}</strong> — indice {publicationTarget.indice}</Typography>
              <Typography color="text.secondary">
                {publicationTarget.stage === 'working'
                  ? 'Publication / mise à jour du fichier source de travail'
                  : (publicationTarget.stage === 'validation' ? 'Publication pour validation interne' : 'Publication du livrable prêt à diffuser')}
              </Typography>
              <Typography color="text.secondary">Destination : {publicationTarget.targetFolderPath}</Typography>
            </div>
          )}
          {publicationState.error && <Typography color="error" mb={1}>{publicationState.error}</Typography>}
          {publicationState.success ? (
            <div className="ged-publish-success">
              <Typography>Fichier publié : <strong>{publicationState.success.fileName}</strong></Typography>
              <Typography color="text.secondary">{publicationState.success.path}</Typography>
              <div className="ged-publish-actions">
                {publicationState.success.openUrl && (
                  <Button variant="secondary" onClick={() => window.open(publicationState.success.openUrl, '_blank', 'noopener,noreferrer')}>
                    Ouvrir dans NextCloud
                  </Button>
                )}
                {projectId && <Link className="basic-button active" to={`/projets/${projectId}/lda`}>Retour à la LDA</Link>}
                {interventionId && <Link className="basic-button active" to={`/interventions/${interventionId}`}>Retour à l’intervention</Link>}
              </div>
            </div>
          ) : (
            <div className="ged-publish-form">
              <input
                type="file"
                onChange={(event) => setPublicationFile(event.target.files?.[0] || null)}
                disabled={!publicationTarget || publicationState.loading}
              />
              <Button
                variant="primary"
                onClick={publishSelectedLdaFile}
                disabled={!publicationTarget || !publicationFile || publicationState.loading}
              >
                {publicationState.loading
                  ? 'Publication…'
                  : (publicationTarget?.existingDocumentId
                    ? (publicationTarget?.stage === 'working' ? 'Mettre à jour dans NextCloud' : 'Remplacer le fichier')
                    : (publicationTarget?.stage === 'working' ? 'Publier le fichier de travail' : 'Publier dans NextCloud'))}
              </Button>
            </div>
          )}
        </section>
      )}

      {gedMode === 'publish-visa' && ldaLineId && (
        <section className="module-panel ged-publish-context">
          <Typography variant="h6" mb={1}>Dépôt VISA LDA</Typography>
          <Typography color="text.secondary">Retrouvez le dépôt VISA depuis la LDA du projet.</Typography>
          {projectId && <Link to={`/projets/${projectId}/lda`}>Accéder à la LDA</Link>}
        </section>
      )}

      <section className="module-panel ged-browser-panel" aria-busy={nextcloudStatus.loading}>
        <div className="ged-search-bar">
          <Input className="ged-search-input" label="Rechercher dans ce dossier" value={search}
            onChange={(event) => setSearch(event.target.value)} placeholder="Nom de fichier, dossier, type…" />
          <button className="ged-breadcrumb-button" type="button" aria-expanded={filtersOpen}
            aria-controls="ged-filters" onClick={() => setFiltersOpen(!filtersOpen)}>Filtres{kind !== 'all' ? ' · 1' : ''}</button>
        </div>
        {filtersOpen && <div id="ged-filters" className="ged-filters">
          <label>Afficher <select value={kind} onChange={(event) => setKind(event.target.value)}>
            <option value="all">Tous les éléments</option><option value="folders">Dossiers</option><option value="files">Fichiers</option>
          </select></label>
          <button type="button" className="ged-breadcrumb-button" onClick={() => { setKind('all'); setSearch(''); }}>Réinitialiser</button>
        </div>}
        <nav className="ged-breadcrumbs" aria-label="Emplacement GED">
          <button type="button" className="ged-breadcrumb-button" onClick={() => navigateFolder(rootPath)} aria-current={atRoot ? 'page' : undefined}>GED</button>
          {breadcrumbs.map((breadcrumb, index) => <React.Fragment key={breadcrumb.path}>
            <span aria-hidden="true">›</span><button type="button" className="ged-breadcrumb-button"
              aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}
              onClick={() => navigateFolder(breadcrumb.path)}>{breadcrumb.label}</button>
          </React.Fragment>)}
        </nav>
        {nextcloudStatus.error && <div role="alert" className="ged-notice">{nextcloudStatus.error}
          <Button variant="secondary" onClick={syncCurrentFolder}>Réessayer</Button>
          <Button variant="secondary" onClick={() => setAccountOpen(true)}>Configurer mon compte</Button>
        </div>}
        {actionMessage && <p role="status">{actionMessage}</p>}
        {showSpaces && <div className="ged-spaces">
          {['Activité', 'Ressources', 'Espace protégé'].map((group) => {
            const entries = folders.filter((document) => spaceGroup(document.name) === group);
            return entries.length > 0 && <section key={group}><h2>{group}</h2><div>
              {entries.map((document) => <button className="ged-space-card" type="button" key={document.id}
                onClick={() => openDocument(document)} disabled={nextcloudStatus.loading}>
                <FolderIcon /><strong>{document.name === 'Templates' ? 'Modèles' : document.name}</strong>
                <span>{group === 'Espace protégé' ? 'Accès selon vos droits' : group === 'Activité' ? 'Espace métier' : 'Ressources partagées'}</span>
              </button>)}
            </div></section>;
          })}
        </div>}
        <div className="ged-toolbar">
          <Typography>{showSpaces ? 'Fichiers et autres dossiers' : 'Contenu du dossier'} · {listedDocuments.length} élément{listedDocuments.length > 1 ? 's' : ''}</Typography>
          <div className="ged-view-controls"><label>Trier par <select value={sort} onChange={(event) => setSort(event.target.value)}>
            <option value="name">Nom</option><option value="recent">Dernière modification</option>
          </select></label><label>Vue <select value={view} onChange={(event) => setView(event.target.value)}>
            <option value="list">Liste</option><option value="grid">Grille</option>
          </select></label></div>
        </div>
        {view === 'grid' ? <div className="ged-document-grid">
          {listedDocuments.map((document) => <article key={document.id || document.nextCloudPath} className="ged-grid-card">
            <button type="button" className="ged-grid-open" onClick={() => openDocument(document)} disabled={nextcloudStatus.loading}>
              <span className={`ged-file-icon ${document.isFolder ? 'folder' : 'file'}`}>{document.isFolder ? <FolderIcon /> : <InsertDriveFileIcon />}</span>
              <strong>{document.name}</strong><span>{documentType(document)} · {formatDate(document.updatedAt)}</span>
            </button>
            <button type="button" className="ged-breadcrumb-button" onClick={() => setInfoDocument(document)} aria-label={`Informations sur ${document.name}`}>Informations</button>
          </article>)}
        </div> : <div className="ged-table-wrap"><table className="ged-table">
          <thead><tr><th scope="col">Nom</th><th scope="col">Type / contexte</th><th scope="col">Modifié</th><th scope="col">Taille</th><th scope="col">Actions</th></tr></thead>
          <tbody>{listedDocuments.map((document) => <tr key={document.id || document.nextCloudPath}>
            <td><button type="button" className="ged-document-open" onClick={() => openDocument(document)} disabled={nextcloudStatus.loading}>
              <span className={`ged-file-icon ${document.isFolder ? 'folder' : 'file'}`}>{document.isFolder ? <FolderIcon /> : <InsertDriveFileIcon />}</span>
              <span>{document.name}</span></button></td>
            <td>{documentType(document)}
              {(document.lda || []).map((line) => <div className="ged-lda-context" key={line.id}>
                {line.projectId ? <Link to={`/projets/${line.projectId}/lda`}>{line.codeGed}</Link> : line.codeGed}
                <span>Indice {line.indice} · {line.status}</span>
              </div>)}
            </td>
            <td>{formatDate(document.updatedAt)}</td><td>{document.isFolder ? '—' : formatSize(document.fileSize || 0)}</td>
            <td><details className="ged-actions"><summary aria-label={`Actions pour ${document.name}`}>⋯</summary><div>
              <button type="button" onClick={() => openDocument(document)}>Ouvrir</button>
              <button type="button" onClick={() => copyDocumentLink(document)}>Copier le lien</button>
              <button type="button" onClick={() => setInfoDocument(document)}>Informations</button>

            </div></details></td>
          </tr>)}</tbody>
        </table></div>}
        {nextcloudStatus.loading && <div className="ged-empty-state" role="status">Chargement des documents…</div>}
        {!nextcloudStatus.loading && !nextcloudStatus.error && listedDocuments.length === 0 && <div className="ged-empty-state">
          {search || kind !== 'all' ? 'Aucun résultat pour cette recherche ou ces filtres.' : showSpaces && folders.length > 0 ? 'Choisissez un espace ci-dessus pour parcourir ses documents.' : 'Ce dossier est vide.'}
        </div>}
      </section>
      <Dialog open={Boolean(infoDocument)} title="Informations du document" onClose={() => setInfoDocument(null)} size="small">
        {infoDocument && <dl className="ged-document-info">
          <dt>Nom</dt><dd>{infoDocument.name}</dd><dt>Emplacement</dt><dd>{infoDocument.nextCloudPath}</dd>
          <dt>Modifié</dt><dd>{formatDate(infoDocument.updatedAt)}</dd>
          <dt>Type</dt><dd>{documentType(infoDocument)}</dd>
          {!infoDocument.isFolder && <><dt>Taille</dt><dd>{formatSize(infoDocument.fileSize || 0)}</dd></>}
          <div className="ged-info-actions"><Button variant="secondary" onClick={() => copyDocumentLink(infoDocument)}>Copier le lien</Button>
            <Button variant="primary" onClick={() => { openDocument(infoDocument); setInfoDocument(null); }}>Ouvrir</Button></div>
        </dl>}
      </Dialog>

      <Dialog
        open={accountOpen}
        title="Compte NextCloud personnel"
        size="small"
        closeOnOverlay={false}
        onClose={() => setAccountOpen(false)}
        actions={(
          <>
            <Button variant="secondary" onClick={testPersonalNextcloudAccount}>
              Tester
            </Button>
            <Button variant="primary" onClick={saveNextcloudAccount}>
              Enregistrer
            </Button>
          </>
        )}
      >
          <div className="ged-account-form">
            <Typography color="text.secondary">
              Compte utilisé pour accéder à vos documents et dossiers.
            </Typography>
            <Input
              fullWidth
              label="Identifiant NextCloud"
              name="nextcloudUsername"
              value={nextcloudAccount.nextcloudUsername}
              onChange={handleAccountChange}
            />
            <Input
              fullWidth
              label={nextcloudAccount.hasNextcloudCredentials ? 'Nouveau mot de passe d’application' : 'Mot de passe d’application'}
              name="nextcloudAppPassword"
              type="password"
              value={nextcloudAccount.nextcloudAppPassword}
              onChange={handleAccountChange}
            />
            <Input
              fullWidth
              label="Dossier racine"
              name="nextcloudRootPath"
              value={nextcloudAccount.nextcloudRootPath}
              onChange={handleAccountChange}
              placeholder="/"
            />
            {accountStatus && (
              <Typography color={accountStatus.severity === 'error' ? 'error' : 'success.main'}>
                {accountStatus.message}
              </Typography>
            )}
          </div>
      </Dialog>
    </Box>
  );
}

export default GedPage;
